import React, { useEffect, useState } from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import {
  getCommunitiesApi,
  getCommunityFeatureFlagsApi,
  updateCommunityFeatureFlagsApi
} from '../../../Redux/util/Apis/selfServe';
import styles from './styles.module.scss';

const FeatureFlags = ({ tenantId }) => {
  const [communities, setCommunities] = useState([]);

  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [flags, setFlags] = useState({});
  const [originalFlags, setOriginalFlags] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: ''
  });

  useEffect(() => {
    if (tenantId) {
      getCommunitiesApi(tenantId)
        .then(res => {
          const data = res?.data || [];
          setCommunities(
            data
              .map(c => ({ _id: c.id, name: c.name }))
              .sort((a, b) => a.name.localeCompare(b.name))
          );
        })
        .catch(() => {
          setCommunities([]);
        });
    }
  }, [tenantId]);

  const fetchFlags = async communityId => {
    setLoading(true);
    try {
      const response = await getCommunityFeatureFlagsApi(communityId);
      const data = response?.data || {};
      setFlags(data);
      setOriginalFlags(data);
    } catch {
      setNotification({ open: true, message: 'Failed to load feature flags' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCommunity) {
      fetchFlags(selectedCommunity);
    }
  }, [selectedCommunity]);

  const handleToggle = key => {
    setFlags(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const hasChanges = JSON.stringify(flags) !== JSON.stringify(originalFlags);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateCommunityFeatureFlagsApi(
        selectedCommunity,
        flags
      );
      const data = response?.data || flags;
      setOriginalFlags(data);
      setFlags(data);
      setNotification({
        open: true,
        message: 'Feature flags updated successfully'
      });
    } catch {
      setNotification({
        open: true,
        message: 'Failed to update feature flags'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFlags({ ...originalFlags });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FormControl size="small" className={styles.communitySelect}>
          <InputLabel>Select Community</InputLabel>
          <Select
            label="Select Community"
            value={selectedCommunity}
            onChange={e => setSelectedCommunity(e.target.value)}
            variant="outlined"
            style={{ minWidth: '300px' }}
          >
            {communities.map(c => (
              <MenuItem key={c._id} value={c._id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedCommunity && hasChanges && (
          <div className={styles.actions}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset}
              disabled={saving}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      {loading && (
        <div className={styles.loader}>
          <CircularProgress />
        </div>
      )}

      {!loading && selectedCommunity && Object.keys(flags).length > 0 && (
        <div className={styles.flagsList}>
          {Object.keys(flags)
            .sort()
            .map(flagKey => (
              <div key={flagKey} className={styles.flagItem}>
                <div className={styles.flagLabel}>{flagKey}</div>
                <Switch
                  checked={!!flags[flagKey]}
                  onChange={() => handleToggle(flagKey)}
                  size="small"
                  color="primary"
                />
              </div>
            ))}
        </div>
      )}

      {!loading && selectedCommunity && Object.keys(flags).length === 0 && (
        <p className={styles.noData}>
          No feature flags configured for this community. Generate config first.
        </p>
      )}

      {!selectedCommunity && !loading && (
        <p className={styles.noData}>
          Select a community to manage feature flags
        </p>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ open: false, message: '' })}
        message={notification.message}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </div>
  );
};

export default FeatureFlags;
